import { PollingState } from '@shared/state/polling';
import { state } from '@shared/state/store';
import { updateStore } from '@shared/state/updateStore';

const DEFAULT_MINIMUM_INTERVAL = 6000;

export function poll<T = unknown>(
  fetchCall: () => Promise<T>,
  report?: (result: Awaited<ReturnType<typeof fetchCall>>) => unknown,
  minimumInterval = DEFAULT_MINIMUM_INTERVAL
): () => void {
  if (isStopped()) {
    startPolling(fetchCall, report, minimumInterval);
  }
  return stopPolling;
}

function startPolling<T>(
  fetchCall: () => Promise<T>,
  report?: (result: Awaited<ReturnType<typeof fetchCall>>) => unknown,
  minimumInterval = DEFAULT_MINIMUM_INTERVAL
): void {
  clearTimeout(state.polling.timer);
  const invoke = async (): Promise<void> => {
    updateState({ isPolling: true, callTime: Date.now() });
    const result = await fetchCall();
    report?.(result);
    startPolling(fetchCall, report, minimumInterval);
  };
  const delay = getDelay(state.polling.callTime, minimumInterval);
  const timer = setTimeout(invoke, delay);
  updateState({ timer });
}

function stopPolling(): void {
  clearTimeout(state.polling.timer);
  updateState({
    isPolling: false,
    callTime: undefined,
    timer: undefined
  });
}

function isStopped(): boolean {
  return state.polling.timer === undefined;
}

function getDelay(
  timeOfLastCall: number | undefined,
  minimumCallInterval: number
): number {
  if (timeOfLastCall === undefined) {
    return 0;
  }
  const elapsed = Date.now() - timeOfLastCall;
  return Math.max(0, minimumCallInterval - elapsed);
}

function updateState(updates: Partial<PollingState>): void {
  updateStore('polling', updates);
}
